import React from 'react';
import styled from 'styled-components';
import { RefreshCcw, Scissors } from 'lucide-react';

import { theme } from '../../styles/theme';
import { PhotoMetadata } from '../../utils/photoProcessing';
import { QualityHints } from './QualityHints';

interface PhotoPreviewProps {
  title: string;
  imageSrc: string;
  metadata: PhotoMetadata;
  warnings?: string[];
  infos?: string[];
  onReplace: () => void;
  onCrop: () => void;
  disabled?: boolean;
  progress?: number | null;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const PreviewCard = styled.div`
  position: relative;
  border-radius: ${theme.borderRadius['3xl']};
  overflow: hidden;
  box-shadow: ${theme.shadows.base};
  background: white;
`;

const ImageWrapper = styled.div`
  position: relative;
  padding-top: 110%;
  background: ${theme.colors.background.secondary};
`;

const PreviewImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${theme.borderRadius['3xl']};
`;

const MetadataList = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin: 0;
`;

const MetadataItem = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 0.75rem;
`;

const MetadataLabel = styled.dt`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const MetadataValue = styled.dd`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.light};
  background: white;
  padding: 0.6rem 1rem;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${theme.colors.text.primary};

  &:hover {
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[500]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  height: 0.5rem;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: ${theme.colors.primary[400]};
  width: ${({ $progress }) => `${$progress}%`};
  transition: width 0.2s ease;
`;

export const PhotoPreview: React.FC<PhotoPreviewProps> = ({
  title,
  imageSrc,
  metadata,
  warnings,
  infos,
  onReplace,
  onCrop,
  disabled,
  progress = null,
}) => {
  return (
    <Container aria-label={`${title} preview`}>
      <PreviewCard>
        <ImageWrapper>
          <PreviewImage src={imageSrc} alt={`${title} preview`} />
        </ImageWrapper>
      </PreviewCard>

      <MetadataList>
        <MetadataItem>
          <MetadataLabel>Resolution</MetadataLabel>
          <MetadataValue>
            {metadata.width} × {metadata.height}px
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataLabel>File Size</MetadataLabel>
          <MetadataValue>{metadata.sizeKB} KB</MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataLabel>Format</MetadataLabel>
          <MetadataValue>{metadata.mime.replace('image/', '').toUpperCase()}</MetadataValue>
        </MetadataItem>
        {typeof metadata.brightness === 'number' && (
          <MetadataItem>
            <MetadataLabel>Brightness</MetadataLabel>
            <MetadataValue>{metadata.brightness}</MetadataValue>
          </MetadataItem>
        )}
        {typeof metadata.facesDetected === 'number' && (
          <MetadataItem>
            <MetadataLabel>Faces</MetadataLabel>
            <MetadataValue>{metadata.facesDetected}</MetadataValue>
          </MetadataItem>
        )}
      </MetadataList>

      {progress !== null && (
        <ProgressBarContainer aria-label="Upload progress">
          <ProgressFill $progress={progress} />
        </ProgressBarContainer>
      )}

      <ActionsRow>
        <ActionButton type="button" onClick={onReplace} disabled={disabled}>
          <RefreshCcw size={16} />
          Replace photo
        </ActionButton>
        <ActionButton type="button" onClick={onCrop} disabled={disabled}>
          <Scissors size={16} />
          Crop / Rotate
        </ActionButton>
      </ActionsRow>

      <QualityHints warnings={warnings} infos={infos} />
    </Container>
  );
};


